import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Tag, Button, Input, Select, Space, Row, Col,
    DatePicker, Modal, Form, message, Spin, Typography, Descriptions,
    Timeline, Tooltip, Statistic, Alert, InputNumber, AutoComplete
} from 'antd';
import {
    SearchOutlined,
    EyeOutlined,
    EditOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ToolOutlined,
    CarOutlined,
    PlusOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const ORDER_TYPES = ['Crown', 'Bridge', 'Denture', 'Partial Denture', 'Implant', 'Veneer', 'Retainer', 'Night Guard', 'Other'];
const MATERIALS = ['Ceramic', 'Porcelain', 'Metal', 'Zirconia', 'Acrylic', 'Composite', 'Gold', 'Titanium', 'Other'];

const LabOrders = () => {
    const { t } = useTranslation(['laboratory', 'common']);
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [detailModal, setDetailModal] = useState(false);
    const [statusModal, setStatusModal] = useState(false);
    const [createModal, setCreateModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [statusNote, setStatusNote] = useState('');
    const [updating, setUpdating] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createForm] = Form.useForm();

    // Patient search
    const [patients, setPatients] = useState([]);
    const [patientSearch, setPatientSearch] = useState('');
    const [selectedPatient, setSelectedPatient] = useState(null);

    // Dentist list
    const [dentists, setDentists] = useState([]);

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (priorityFilter) params.priority = priorityFilter;
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.startDate = dateRange[0].format('YYYY-MM-DD');
                params.endDate = dateRange[1].format('YYYY-MM-DD');
            }

            const res = await api.get('/lab/orders', { params });
            setOrders(res.data?.data || []);
        } catch (err) {
            message.error(err.response?.data?.message || t('laboratory:messages.fetchFailed'));
        } finally {
            setLoading(false);
        }
    }, [statusFilter, priorityFilter, dateRange]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    useEffect(() => {
        let filtered = [...orders];
        if (searchText) {
            const lower = searchText.toLowerCase();
            filtered = filtered.filter(o =>
                o.orderNumber?.toLowerCase().includes(lower) ||
                o.patientId?.name?.toLowerCase().includes(lower) ||
                o.orderType?.toLowerCase().includes(lower) ||
                o.description?.toLowerCase().includes(lower)
            );
        }
        setFilteredOrders(filtered);
    }, [orders, searchText]);

    // Fetch patients for autocomplete - filtered by selected dentist
    const searchPatients = async (value) => {
        setPatientSearch(value);
        if (value.length < 2) { setPatients([]); return; }
        try {
            const dentistUserId = createForm.getFieldValue('dentistId');
            // Find the dentist doc _id from the userId for patient filtering
            const dentist = dentists.find(d => String(d.userId) === String(dentistUserId));
            let url = `/patients?search=${value}&limit=20`;
            if (dentist) url += `&dentistId=${dentist._id}`;
            const res = await api.get(url);
            setPatients(res.data?.data || []);
        } catch { setPatients([]); }
    };

    // Fetch dentists
    const fetchDentists = async () => {
        try {
            const res = await api.get('/dentists', { params: { limit: 1000, isActive: true } });
            const dentistList = (res.data?.data || []).map(d => ({
                _id: String(d._id),
                userId: String(d.userId?._id || d.userId || ''),
                name: d.name || ''
            }));
            setDentists(dentistList);
        } catch { setDentists([]); }
    };

    const handleCreateOrder = async () => {
        try {
            const values = await createForm.validateFields();
            setCreating(true);

            const orderData = {
                ...(selectedPatient ? { patientId: selectedPatient } : {}),
                patientName: patientSearch || undefined,
                dentistId: values.dentistId || undefined,
                orderType: values.orderType,
                description: values.description,
                material: values.material,
                shade: values.shade,
                teethNumbers: values.teethNumbers ? values.teethNumbers.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n)) : [],
                priority: values.priority || 'Normal',
                expectedDeliveryDate: values.expectedDeliveryDate?.toISOString(),
                cost: values.cost,
                currency: values.currency || 'USD',
                notes: values.notes
            };

            await api.post('/lab/orders', orderData);
            message.success(t('laboratory:messages.createSuccess'));
            setCreateModal(false);
            createForm.resetFields();
            setSelectedPatient(null);
            setPatientSearch('');
            fetchOrders();
        } catch (err) {
            if (err.errorFields) return; // form validation error
            message.error(err.response?.data?.message || t('laboratory:messages.createFailed'));
        } finally {
            setCreating(false);
        }
    };

    const openCreateModal = () => {
        createForm.resetFields();
        createForm.setFieldsValue({ currency: 'USD', priority: 'Normal', material: 'Ceramic' });
        setSelectedPatient(null);
        setPatientSearch('');
        fetchDentists();
        setCreateModal(true);
    };

    const handleStatusUpdate = async () => {
        if (!selectedOrder || !newStatus) return;
        try {
            setUpdating(true);
            await api.put(`/lab/orders/${selectedOrder._id}/status`, {
                status: newStatus,
                notes: statusNote
            });
            message.success(t('laboratory:messages.updateSuccess'));
            setStatusModal(false);
            setNewStatus('');
            setStatusNote('');
            setSelectedOrder(null);
            fetchOrders();
        } catch (err) {
            message.error(err.response?.data?.message || t('laboratory:messages.updateFailed'));
        } finally {
            setUpdating(false);
        }
    };

    const openDetailModal = async (order) => {
        try {
            const res = await api.get(`/lab/orders/${order._id}`);
            setSelectedOrder(res.data?.data || order);
            setDetailModal(true);
        } catch {
            setSelectedOrder(order);
            setDetailModal(true);
        }
    };

    const openStatusModal = (order) => {
        setSelectedOrder(order);
        setNewStatus('');
        setStatusNote('');
        setStatusModal(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending': return 'orange';
            case 'In Progress': return 'blue';
            case 'Completed': return 'green';
            case 'Delivered': return 'cyan';
            case 'Cancelled': return 'red';
            default: return 'default';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Urgent': return 'red';
            case 'Emergency': return 'magenta';
            default: return 'blue';
        }
    };

    const getNextStatuses = (currentStatus) => {
        switch (currentStatus) {
            case 'Pending': return ['In Progress', 'Cancelled'];
            case 'In Progress': return ['Completed', 'Cancelled'];
            case 'Completed': return ['Delivered'];
            default: return [];
        }
    };

    const columns = [
        {
            title: t('laboratory:orders.orderNumber'),
            dataIndex: 'orderNumber',
            key: 'orderNumber',
            width: 140,
            render: (text) => <strong>{text}</strong>
        },
        {
            title: t('laboratory:orders.patient'),
            key: 'patient',
            render: (_, record) => record.patientId?.name || record.patientName || 'N/A'
        },
        {
            title: t('common:dentist', 'Dentist'),
            key: 'dentist',
            render: (_, record) => {
                if (!record.dentistId) return 'N/A';
                if (typeof record.dentistId === 'string') return record.dentistId;
                return record.dentistId.name || record.dentistId.email || 'N/A';
            }
        },
        {
            title: t('laboratory:orders.type'),
            dataIndex: 'orderType',
            key: 'orderType',
            render: (type) => <Tag>{type}</Tag>
        },
        {
            title: t('laboratory:orders.material'),
            dataIndex: 'material',
            key: 'material',
        },
        {
            title: t('laboratory:orders.priority'),
            dataIndex: 'priority',
            key: 'priority',
            render: (priority) => <Tag color={getPriorityColor(priority)}>{t(`laboratory:orders.priorities.${priority}`, priority)}</Tag>
        },
        {
            title: t('laboratory:orders.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => <Tag color={getStatusColor(status)}>{t(`laboratory:orders.statuses.${status}`, status)}</Tag>
        },
        {
            title: t('laboratory:orders.expectedDelivery'),
            dataIndex: 'expectedDeliveryDate',
            key: 'expectedDelivery',
            render: (date) => {
                if (!date) return '-';
                const isOverdue = new Date(date) < new Date() && !['Completed', 'Delivered', 'Cancelled'].includes('');
                return (
                    <span style={{ color: isOverdue ? '#ff4d4f' : undefined }}>
                        {dayjs(date).format('MMM DD, YYYY')}
                    </span>
                );
            }
        },
        {
            title: t('laboratory:orders.cost'),
            dataIndex: 'cost',
            key: 'cost',
            render: (cost, record) => {
                if (!cost) return '-';
                return record.currency === 'IQD'
                    ? `${cost.toLocaleString()} IQD`
                    : `$${cost.toFixed(2)}`;
            }
        },
        {
            title: t('laboratory:orders.actions'),
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Details">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => openDetailModal(record)}
                        />
                    </Tooltip>
                    {!['Delivered', 'Cancelled'].includes(record.status) && (
                        <Tooltip title="Update Status">
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => openStatusModal(record)}
                                style={{ color: '#1890ff' }}
                            />
                        </Tooltip>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={2} style={{ margin: 0 }}>{t('laboratory:orders.title')}</Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                    {t('laboratory:orders.newOrder')}
                </Button>
            </div>

            {/* Filters */}
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            placeholder={t('laboratory:orders.searchPlaceholder')}
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <Select
                            placeholder={t('laboratory:orders.filterByStatus')}
                            value={statusFilter || undefined}
                            onChange={v => setStatusFilter(v || '')}
                            allowClear
                            style={{ width: '100%' }}
                        >
                            <Option value="Pending">{t('laboratory:orders.statuses.Pending')}</Option>
                            <Option value="In Progress">{t('laboratory:orders.statuses.In Progress')}</Option>
                            <Option value="Completed">{t('laboratory:orders.statuses.Completed')}</Option>
                            <Option value="Delivered">{t('laboratory:orders.statuses.Delivered')}</Option>
                            <Option value="Cancelled">{t('laboratory:orders.statuses.Cancelled')}</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <Select
                            placeholder={t('laboratory:orders.filterByPriority')}
                            value={priorityFilter || undefined}
                            onChange={v => setPriorityFilter(v || '')}
                            allowClear
                            style={{ width: '100%' }}
                        >
                            <Option value="Normal">{t('laboratory:orders.priorities.Normal')}</Option>
                            <Option value="Urgent">{t('laboratory:orders.priorities.Urgent')}</Option>
                            <Option value="Emergency">{t('laboratory:orders.priorities.Rush', 'Emergency')}</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <RangePicker
                            style={{ width: '100%' }}
                            onChange={(dates) => setDateRange(dates)}
                            value={dateRange}
                        />
                    </Col>
                </Row>
            </Card>

            {/* Stats Row */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={6}>
                    <Card size="small">
                        <Statistic title={t('laboratory:orders.totalOrders')} value={filteredOrders.length} />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small">
                        <Statistic
                            title={t('laboratory:orders.statuses.Pending')}
                            value={filteredOrders.filter(o => o.status === 'Pending').length}
                            valueStyle={{ color: '#faad14' }}
                            prefix={<ClockCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small">
                        <Statistic
                            title={t('laboratory:orders.statuses.In Progress')}
                            value={filteredOrders.filter(o => o.status === 'In Progress').length}
                            valueStyle={{ color: '#1890ff' }}
                            prefix={<ToolOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small">
                        <Statistic
                            title={t('laboratory:orders.statuses.Completed')}
                            value={filteredOrders.filter(o => o.status === 'Completed').length}
                            valueStyle={{ color: '#52c41a' }}
                            prefix={<CheckCircleOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Orders Table */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={filteredOrders}
                    rowKey="_id"
                    loading={loading}
                    pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (total) => `Total: ${total} orders` }}
                    scroll={{ x: 1100 }}
                    locale={{ emptyText: t('laboratory:dashboard.noOrdersFound') }}
                />
            </Card>

            {/* Order Detail Modal */}
            <Modal
                title={`${t('laboratory:orders.orderDetails')} - ${selectedOrder?.orderNumber || ''}`}
                open={detailModal}
                onCancel={() => { setDetailModal(false); setSelectedOrder(null); }}
                footer={[
                    <Button key="close" onClick={() => { setDetailModal(false); setSelectedOrder(null); }}>
                        Close
                    </Button>,
                    selectedOrder && !['Delivered', 'Cancelled'].includes(selectedOrder.status) && (
                        <Button
                            key="update"
                            type="primary"
                            onClick={() => { setDetailModal(false); openStatusModal(selectedOrder); }}
                        >
                            Update Status
                        </Button>
                    )
                ]}
                width={700}
            >
                {selectedOrder && (
                    <>
                        <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="Order Number">{selectedOrder.orderNumber}</Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={getStatusColor(selectedOrder.status)}>{selectedOrder.status}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Patient">{selectedOrder.patientId?.name || selectedOrder.patientName || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Dentist">{typeof selectedOrder.dentistId === 'object' ? (selectedOrder.dentistId?.name || selectedOrder.dentistId?.email || 'N/A') : (selectedOrder.dentistId || 'N/A')}</Descriptions.Item>
                            <Descriptions.Item label="Order Type">{selectedOrder.orderType}</Descriptions.Item>
                            <Descriptions.Item label="Material">{selectedOrder.material || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Priority">
                                <Tag color={getPriorityColor(selectedOrder.priority)}>{selectedOrder.priority}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Shade">{selectedOrder.shade || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Teeth Numbers">
                                {selectedOrder.teethNumbers?.join(', ') || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Cost">
                                {selectedOrder.cost
                                    ? selectedOrder.currency === 'IQD'
                                        ? `${selectedOrder.cost.toLocaleString()} IQD`
                                        : `$${selectedOrder.cost.toFixed(2)}`
                                    : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Order Date" span={2}>
                                {dayjs(selectedOrder.orderDate).format('MMM DD, YYYY')}
                            </Descriptions.Item>
                            <Descriptions.Item label="Expected Delivery" span={2}>
                                {selectedOrder.expectedDeliveryDate
                                    ? dayjs(selectedOrder.expectedDeliveryDate).format('MMM DD, YYYY')
                                    : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Description" span={2}>
                                {selectedOrder.description || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Notes" span={2}>
                                {selectedOrder.notes || 'N/A'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Card title="Timeline" size="small">
                            <Timeline
                                items={[
                                    {
                                        color: 'green',
                                        children: `Order Created: ${dayjs(selectedOrder.createdAt).format('MMM DD, YYYY HH:mm')}`
                                    },
                                    ...(selectedOrder.workStartedDate ? [{
                                        color: 'blue',
                                        children: `Work Started: ${dayjs(selectedOrder.workStartedDate).format('MMM DD, YYYY HH:mm')}`
                                    }] : []),
                                    ...(selectedOrder.workCompletedDate ? [{
                                        color: 'green',
                                        children: `Work Completed: ${dayjs(selectedOrder.workCompletedDate).format('MMM DD, YYYY HH:mm')}`
                                    }] : []),
                                    ...(selectedOrder.deliveredDate ? [{
                                        color: 'cyan',
                                        children: `Delivered: ${dayjs(selectedOrder.deliveredDate).format('MMM DD, YYYY HH:mm')}`
                                    }] : [])
                                ]}
                            />
                        </Card>

                        {selectedOrder.attachments?.length > 0 && (
                            <Card title="Attachments" size="small" style={{ marginTop: 16 }}>
                                {selectedOrder.attachments.map((att, i) => (
                                    <div key={i} style={{ marginBottom: 8 }}>
                                        <a href={att.fileUrl} target="_blank" rel="noopener noreferrer">
                                            {att.fileName}
                                        </a>
                                        {att.description && <span style={{ color: '#999', marginLeft: 8 }}>- {att.description}</span>}
                                    </div>
                                ))}
                            </Card>
                        )}
                    </>
                )}
            </Modal>

            {/* Status Update Modal */}
            <Modal
                title={`Update Status - ${selectedOrder?.orderNumber || ''}`}
                open={statusModal}
                onCancel={() => { setStatusModal(false); setSelectedOrder(null); }}
                onOk={handleStatusUpdate}
                confirmLoading={updating}
                okText={t('laboratory:orders.saveChanges', 'Update Status')}
                okButtonProps={{ disabled: !newStatus }}
            >
                {selectedOrder && (
                    <Form layout="vertical">
                        <Form.Item label="Current Status">
                            <Tag color={getStatusColor(selectedOrder.status)} style={{ fontSize: 14, padding: '4px 12px' }}>
                                {selectedOrder.status}
                            </Tag>
                        </Form.Item>
                        <Form.Item label="New Status" required>
                            <Select
                                placeholder="Select new status"
                                value={newStatus || undefined}
                                onChange={setNewStatus}
                                style={{ width: '100%' }}
                            >
                                {getNextStatuses(selectedOrder.status).map(s => (
                                    <Option key={s} value={s}>{s}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item label="Notes (optional)">
                            <TextArea
                                rows={3}
                                value={statusNote}
                                onChange={e => setStatusNote(e.target.value)}
                                placeholder="Add notes about this status change..."
                            />
                        </Form.Item>
                        {newStatus === 'Completed' && (
                            <Alert
                                message="An invoice will be automatically generated when the order is marked as completed."
                                type="info"
                                showIcon
                                style={{ marginTop: 8 }}
                            />
                        )}
                    </Form>
                )}
            </Modal>

            {/* Create Order Modal */}
            <Modal
                title={t('laboratory:orders.newOrder')}
                open={createModal}
                onCancel={() => { setCreateModal(false); createForm.resetFields(); setSelectedPatient(null); }}
                onOk={handleCreateOrder}
                confirmLoading={creating}
                okText={t('laboratory:orders.placeOrder')}
                width={700}
            >
                <Form form={createForm} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="dentistId" label="Dentist">
                                <Select
                                    placeholder="Select dentist (optional)"
                                    allowClear
                                    onChange={() => { setSelectedPatient(null); setPatientSearch(''); setPatients([]); }}
                                >
                                    {dentists.map(d => (
                                        <Option key={d.userId} value={d.userId}>{d.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Patient">
                                <AutoComplete
                                    value={patientSearch}
                                    options={patients.map(p => ({
                                        value: p._id,
                                        label: `${p.name} - ${p.patientId || p.contact || ''}`
                                    }))}
                                    onSearch={searchPatients}
                                    onSelect={(value, option) => {
                                        setSelectedPatient(value);
                                        setPatientSearch(option.label);
                                    }}
                                    onChange={(value) => {
                                        if (!value) { setSelectedPatient(null); }
                                        setPatientSearch(value);
                                    }}
                                    placeholder="Search or type patient name..."
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="orderType" label="Order Type" rules={[{ required: true, message: 'Required' }]}>
                                <Select placeholder="Select type">
                                    {ORDER_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="material" label="Material">
                                <Select placeholder="Select material">
                                    {MATERIALS.map(m => <Option key={m} value={m}>{m}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="description" label="Description">
                        <TextArea rows={2} placeholder="Describe the lab work needed..." />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="shade" label="Shade">
                                <Input placeholder="e.g. A2, B1" />
                            </Form.Item>
                        </Col>
                        <Col span={16}>
                            <Form.Item name="teethNumbers" label="Teeth Numbers">
                                <Input placeholder="e.g. 11, 12, 21 (comma separated)" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="priority" label="Priority">
                                <Select>
                                    <Option value="Normal">Normal</Option>
                                    <Option value="Urgent">Urgent</Option>
                                    <Option value="Emergency">Emergency</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="expectedDeliveryDate" label="Expected Delivery" rules={[{ required: true, message: 'Required' }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="cost" label="Cost" rules={[{ required: true, message: 'Required' }]}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="currency" label="Currency">
                                <Select>
                                    <Option value="USD">USD</Option>
                                    <Option value="IQD">IQD</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="notes" label="Notes">
                        <TextArea rows={2} placeholder="Additional notes..." />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default LabOrders;
